// src/components/menuData.js

// ─── DYNAMIC (backend-driven) MENU ──────────────────────────────────────────
// Fetches the per-company menu tree from GET /admin/menu-tree (resolved via
// the company's plan — see ReonBE/controllers/MenuController.js). Each node
// carries ModuleKey (added by 04_menu_master_modulekey_migration.sql) so
// child items can still be gated by RightsContext.canAccess(moduleKey, action)
// the same way the static menuData below already is.
import { useState, useEffect } from "react";
import axios from "axios";
// ─── Icons used only by the commented-out static menu below ────────────────
// import DashboardIcon from "@mui/icons-material/Dashboard";
// import BusinessIcon from "@mui/icons-material/Business";
// import CategoryIcon from "@mui/icons-material/Category";
// import Inventory2Icon from "@mui/icons-material/Inventory2";
// import TrendingUpIcon from "@mui/icons-material/TrendingUp";
// import PersonIcon from "@mui/icons-material/Person";
// import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
// import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
// import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
// import LocalShippingIcon from "@mui/icons-material/LocalShipping";
// import ReceiptIcon from "@mui/icons-material/Receipt";
// import AssignmentIcon from "@mui/icons-material/Assignment";
// import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
// import PrintIcon from "@mui/icons-material/Print";
// import InventoryIcon from "@mui/icons-material/Inventory";
// import BarChartIcon from "@mui/icons-material/BarChart";
// import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
// import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
// import NoteAltIcon from "@mui/icons-material/NoteAlt";
// import MoveToInboxIcon from "@mui/icons-material/MoveToInbox";
// import OutboxIcon from "@mui/icons-material/Outbox";
// import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
// import SummarizeIcon from "@mui/icons-material/Summarize";
// import WarehouseIcon from "@mui/icons-material/Warehouse";
// import SettingsIcon from "@mui/icons-material/Settings";
// import CallReceivedIcon from "@mui/icons-material/CallReceived";
// import CallMadeIcon from "@mui/icons-material/CallMade";
// import DescriptionIcon from "@mui/icons-material/Description";
// import HistoryIcon from "@mui/icons-material/History";
// import BuildIcon from '@mui/icons-material/Build';
// import StorageIcon from "@mui/icons-material/Storage";
// import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
// import InsertChartIcon from "@mui/icons-material/InsertChart";
// import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";

function EmojiIcon({ icon, size }) {
    return <span style={{ fontSize: size, lineHeight: 1 }}>{icon}</span>;
}

function buildMenuDataFromTree(tree) {
    const menuData = {};
    tree.forEach((node) => {
        const key = `menu_${node.MenuId}`;
        const entry = {
            label: node.Label,
            icon: <EmojiIcon icon={node.Icon} size="1.2rem" />,
        };
        if (node.Children && node.Children.length > 0) {
            entry.subFields = {};
            node.Children.forEach((child) => {
                entry.subFields[`menu_${child.MenuId}`] = {
                    label: child.Label,
                    link: child.Link,
                    icon: <EmojiIcon icon={child.Icon} size="1.1rem" />,
                    moduleKey: child.ModuleKey || undefined,
                };
            });
        } else {
            entry.link = node.Link;
            entry.moduleKey = node.ModuleKey || undefined;
        }
        menuData[key] = entry;
    });
    return menuData;
}

// Returns { menuData, loading, error } so callers can render a loading
// skeleton while the tree is in flight and show nothing (not the static
// fallback) if the request fails — per-company menu must reflect the
// backend's resolved plan, not silently mask an outage with stale data.
//
// compId is the logged-in company's own CompId (not the AppRegID's
// parent/admin company). Passing it lets the backend check
// companymaster.AppFeatures for THIS company first (resolvedVia:
// "company_override") before falling back to the AppRegID's plan — without
// it, a per-company plan override set in the admin panel is silently
// ignored and every company under the AppRegID sees the same menu.
export function useMenuData(connectionString, appRegId, compId) {
    const [menuData, setMenuData] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!connectionString || !appRegId) {
            setMenuData({});
            setLoading(false);
            setError(null);
            return;
        }
        let cancelled = false;
        setLoading(true);
        setError(null);

        const params = { AppRegId: appRegId };
        if (compId) params.CompId = compId;

        axios.get(`${connectionString}/admin/menu-tree`, { params })
            .then((res) => {
                if (cancelled) return;
                const tree = res?.data?.Menus || [];
                setMenuData(buildMenuDataFromTree(tree));
                setLoading(false);
            })
            .catch((err) => {
                if (cancelled) return;
                setMenuData({});
                setError(err);
                setLoading(false);
            });

        return () => { cancelled = true; };
    }, [connectionString, appRegId, compId]);

    return { menuData, loading, error };
}

// ─── STATIC MENU — commented out; the app now runs on the dynamic tree ──────
// Kept for reference only. Menu.jsx's `menuDataOverride || menuData` fallback
// and OldNavigation.js (dead/unimported) both referenced this default export;
// Navigation.js always supplies menuDataOverride now so that fallback path
// is not exercised in practice.
// const menuData = {
//     home: {
//         label: "Dashboard",
//         link: "/main/home",
//         icon: <DashboardIcon />
//     },
//     CompanyMaster: {
//         label: "Master",
//         icon: <StorageIcon />,
//         subFields: {
//             Company: { label: "Company", link: "/main/company", icon: <BusinessIcon />, moduleKey: "Company" },
//             client: { label: "Account", link: "/main/v2/ClentDetails", icon: <AccountBalanceWalletIcon />, moduleKey: "Account" },
//             Product2: { label: "Product", link: "/main/v2/productDetail", icon: <Inventory2Icon />, moduleKey: "Product" },
//             BankCashPosting: { label: "BankCash Posting", link: "/main/BankCashPosting", icon: <Inventory2Icon />, moduleKey: "BankCashPosting" },
//             AddOnCharges: { label: "Add-On Charges", link: "/main/AddOnCharges", icon: <Inventory2Icon />, moduleKey: "AddOnCharges" },
//         }
//     },
//     Sale: {
//         label: "Transaction",
//         icon: <PointOfSaleIcon />,
//         subFields: {
//             Quotation: { label: "Quotation", link: "/main/QuatationDetails", icon: <RequestQuoteIcon />, moduleKey: "Quotation" },
//             Orderv2: { label: "Sales Order", link: "/main/v2/orderDetail-stepper", icon: <ShoppingCartIcon />, moduleKey: "SalesOrder" },
//             proformaInvoice: { label: "Proforma Invoice", link: "/main/proforma-invoice", icon: <NoteAltIcon />, moduleKey: "ProformaInvoice" },
//             Dispatch: { label: "Sale Challan", link: "/main/order-dispatch", icon: <OutboxIcon />, moduleKey: "SaleChallan" },
//             saleInvoice: { label: "Sale Invoice", link: "/main/v2/sale-invoice", icon: <ReceiptLongIcon />, moduleKey: "SaleInvoice" },
//             saleInvoicePrevFY: { label: "Sale Invoice (Prev FY)", link: "/main/v2/sale-invoice-prev-fy", icon: <HistoryIcon />, moduleKey: "SaleInvoicePrevFY" },
//             CreditNote: { label: "Credit Note", link: "/main/CreditNote-Details", icon: <TrendingUpIcon />, moduleKey: "CreditNote" },
//             purcOrder: { label: "Purchase Order", link: "/main/purchase-orderDetails", icon: <ShoppingBagIcon />, moduleKey: "PurchaseOrder" },
//             purcDispatch: { label: "Purchase Challan", link: "/main/Purchase-ChallanDetails", icon: <MoveToInboxIcon />, moduleKey: "PurchaseChallan" },
//             purcInvoice: { label: "Purchase Invoice", link: "/main/Purchase-InvoiceDetails", icon: <ReceiptIcon />, moduleKey: "PurchaseInvoice" },
//             purcInvoicePrevFY: { label: "Purc Invoice (Prev FY)", link: "/main/v2/purchase-invoice-prev-fy", icon: <HistoryIcon />, moduleKey: "PurchaseInvoicePrevFY" },
//             DebitNote: { label: "Debit Note", link: "/main/DebitNote-Details", icon: <AssignmentIcon />, moduleKey: "DebitNote" },
//             store: { label: "Production", link: "/main/store-details", icon: <PrecisionManufacturingIcon /> },
//             ReceivableV2: { label: "Receivable", link: "/main/receivable-details", icon: <CallReceivedIcon /> },
//             PayableV2: { label: "Payable", link: "/main/payable-details", icon: <CallMadeIcon /> },
//             Vouchers: { label: "Vouchers", link: "/main/VoucherDetails", icon: <DescriptionIcon /> }
//         }
//     },
//     Reports: {
//         label: "Reports",
//         icon: <InsertChartIcon />,
//         subFields: {
//             AccountReports: { label: "Account Reports", link: "/main/account-report", icon: <SummarizeIcon />, moduleKey: "AccountReport" },
//             OSandODBills: { label: " OS Reports", link: "/main/OSandOD-bills-report", icon: <WarehouseIcon />, moduleKey: "OSandODBillsReport" },
//             GSTReports: { label: "GST Reports", link: "/main/gst-reports", icon: <SummarizeIcon />, moduleKey: "GSTReports" },
//         }
//     },
//     PrintingModule: {
//         label: "Settings",
//         icon: <SettingsIcon />,
//         subFields: {
//             PrintingModule: { label: "Printing Utilities", link: "/main/PrintingUtilities", icon: <PrintIcon />, moduleKey: "PrintingUtilities" },
//             UserDefine: { label: "User Define", link: "/main/user-define-columns", icon: <SettingsIcon />, moduleKey: "UserDefine" },
//             Configure: { label: "Configure", link: "/main/configure", icon: <SettingsIcon />, moduleKey: "Configure" },
//             PriceList: { label: "PriceList", link: "/main/pricelist", icon: <SettingsIcon />, moduleKey: "PriceList" },
//         }
//     },
//     usermanagement: {
//         label: "User Management",
//         icon: <AdminPanelSettingsIcon />,
//         subFields: {
//             userdetails: { label: "User Master", link: "/main/v3/userdetails", icon: <ManageAccountsIcon />, moduleKey: "UserMaster" },
//         }
//     },
// };

// export default menuData;